import React, { useCallback, useEffect, useRef, useState } from "react";

import * as Popover from "@radix-ui/react-popover";

import { useCurrentFullscreenElement } from "~/common/Fullscreen";
import { ControlTooltip, FellowVideoPlayerButton } from "~/meetingRecorder/Video";
import { ToggleGroup } from "~/ui/legacyFellowIndex";

import styles from "./Video.module.css";

interface PlaybackRateSelectorProps {
	videoRef: React.RefObject<HTMLVideoElement>;
	onPlaybackRateChange(playbackRate: number): void;
}

const playbackRateOptions = [
	{ label: "1x", value: "1" },
	{ label: "1.25x", value: "1.25" },
	{ label: "1.5x", value: "1.5" },
	{ label: "1.75x", value: "1.75" },
	{ label: "2x", value: "2" },
];

export default function PlaybackRateSelector({ videoRef, onPlaybackRateChange }: PlaybackRateSelectorProps) {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const currentFullscreenElement = useCurrentFullscreenElement();
	const popeverRef = useRef<HTMLDivElement>(null);
	const toggleOpen = useCallback(() => setIsOpen(isOpen => !isOpen), []);

	const onChange = useCallback(
		(value: string) => {
			onPlaybackRateChange(parseFloat(value));
			setIsOpen(false);
		},
		[onPlaybackRateChange],
	);

	const handleClickOutside = useCallback((event: MouseEvent) => {
		if (popeverRef.current && !popeverRef.current.contains(event.target as Node)) {
			setIsOpen(false);
		}
	}, []);

	useEffect(() => {
		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen, handleClickOutside]);

	return (
		<FellowVideoPlayerButton>
			<span slot="icon">
				<Popover.Root open={isOpen}>
					<Popover.Trigger className={styles.playbackRateTrigger} onClick={toggleOpen}>
						<ControlTooltip content="Playback speed">
							<span>{videoRef.current?.playbackRate ?? "1"}x</span>
						</ControlTooltip>
					</Popover.Trigger>
					<Popover.Portal container={currentFullscreenElement ?? undefined}>
						<Popover.Content side="top" className={styles.playbackRateSelector} ref={popeverRef}>
							<ToggleGroup
								options={playbackRateOptions}
								className={styles.playbackRateGroup}
								itemClassName={styles.playbackRateItem}
								value={videoRef.current?.playbackRate.toString() ?? "1"}
								onValueChange={onChange}
							/>
						</Popover.Content>
					</Popover.Portal>
				</Popover.Root>
			</span>
		</FellowVideoPlayerButton>
	);
}
